import React from 'react';
import { ChevronDown } from 'lucide-react';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import SwipeableTask from './SwipeableTask';

const TIME_SLOT_ORDER = ['morning_peak', 'off_peak', 'evening_peak'];

const TIME_SLOT_LABELS = {
  morning_peak: 'Morning Peak (6-10 AM)',
  off_peak: 'Off-Peak (10 AM-4 PM)',
  evening_peak: 'Evening Peak (4-10 PM)',
};

/**
 * Determine if a time slot is in the past relative to current slot.
 */
function isSlotPast(timeSlot, currentTimeSlot) {
  const slotIdx = TIME_SLOT_ORDER.indexOf(timeSlot);
  const currentIdx = TIME_SLOT_ORDER.indexOf(currentTimeSlot);
  return currentIdx >= 0 && slotIdx >= 0 && slotIdx < currentIdx;
}

/**
 * TimeAdaptiveSection - Collapsible section grouping tasks by time slot.
 * Current time slot is expanded by default with a highlighted border and "NOW" badge.
 * Past slots are collapsed and slightly dimmed. Future slots are collapsed normally.
 */
export default function TimeAdaptiveSection({
  title,
  timeSlot,
  currentTimeSlot,
  tasks,
  onComplete,
  onUncomplete,
}) {
  const isCurrent = timeSlot === currentTimeSlot;
  const isPast = isSlotPast(timeSlot, currentTimeSlot);
  const completedCount = tasks.filter((t) => t.status === 'completed').length;
  const label = title || TIME_SLOT_LABELS[timeSlot] || timeSlot;

  return (
    <Collapsible defaultOpen={isCurrent || (!isPast && !isCurrent && tasks.length > 0 && currentTimeSlot === 'closed')}>
      <div
        className={`rounded-lg border mb-3 overflow-hidden ${
          isCurrent
            ? 'border-l-4 border-l-blue-500 border-t border-r border-b border-slate-200 bg-white'
            : isPast
            ? 'border-slate-200 bg-slate-50 opacity-75'
            : 'border-slate-200 bg-white'
        }`}
      >
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <h3
                className={`font-semibold text-lg ${
                  isPast ? 'text-slate-400' : 'text-slate-800'
                }`}
              >
                {label}
              </h3>
              {isCurrent && (
                <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                  NOW
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">
                {completedCount}/{tasks.length}
              </span>
              <ChevronDown className="w-4 h-4 text-slate-400 transition-transform data-[state=open]:rotate-180" />
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-t border-slate-100">
            {tasks.length === 0 ? (
              <p className="px-4 py-3 text-sm text-slate-400 text-center">
                No tasks in this slot
              </p>
            ) : (
              tasks.map((task) => (
                <SwipeableTask
                  key={task.task_id}
                  task={task}
                  onComplete={onComplete}
                  onUncomplete={onUncomplete}
                />
              ))
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
